import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { ServerContext, StoreContext } from '../../App';
import { TUnlockedAchievement } from '../../services/server/types';
import { achievementEvents } from '../../utils/achievementEvents';
import './AchievementNotification.scss';

const AchievementNotification: React.FC = () => {
    const server = useContext(ServerContext);
    const store = useContext(StoreContext);
    const [queue, setQueue] = useState<TUnlockedAchievement[]>([]);
    const [current, setCurrent] = useState<TUnlockedAchievement | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Обработчик закрытия уведомления
    const handleClose = useCallback(async (achievementId: number) => {
        // Проверяем авторизацию перед вызовом API
        if (store.getToken()) {
            await server.markAchievementNotified(achievementId);
        }
        setCurrent(null);
    }, [server, store]);

    // Слушаем события о новых достижениях (мгновенно)
    useEffect(() => {
        const unsubscribe = achievementEvents.subscribe((newAchievements) => {
            setQueue(prev => [...prev, ...newAchievements]);
        });

        return unsubscribe;
    }, []);

    // Проверка пропущенных уведомлений при загрузке страницы
    useEffect(() => {
        const checkAchievements = async () => {
            // Проверяем авторизацию перед вызовом API
            if (!store.getToken()) {
                return;
            }
            
            const achievements = await server.getUnnotifiedAchievements();
            if (achievements && achievements.length > 0) {
                setQueue(prev => {
                    const existingIds = new Set(prev.map(a => a.id));
                    const newOnes = achievements.filter(a => !existingIds.has(a.id));
                    return [...prev, ...newOnes];
                });
            }
        };

        checkAchievements();
    }, [server, store]);

    // Показ следующего достижения из очереди
    useEffect(() => {
        if (!current && queue.length > 0) {
            const [next, ...rest] = queue;
            setCurrent(next);
            setQueue(rest);

            // Автоматически скрыть через 5 секунд
            timeoutRef.current = setTimeout(() => {
                handleClose(next.id);
            }, 100000);
        }

        // Очистка таймера при размонтировании или изменении current
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [current, queue, handleClose]);

    if (!current) return null;

    return (
        <div className="achievement-notification">
            <div className="achievement-notification-content">
                <div className="achievement-notification-icon">
                    <img 
                        src={current.icon_path} 
                        alt={current.name}
                    />
                </div>
                <div className="achievement-notification-text">
                    <h4>Достижение разблокировано!</h4>
                    <p className="achievement-notification-name">{current.name}</p>
                    <p className="achievement-notification-desc">{current.description}</p>
                </div>
                <button 
                    className="achievement-notification-close"
                    onClick={() => handleClose(current.id)}
                >
                    ✕
                </button>
            </div>
        </div>
    );
};

export default AchievementNotification;

